/**
 * Placing a worker's errors under the fields they are about on the worker card, so a flagged worker says
 * what is wrong without being opened.
 */
import type { FieldErrors } from 'react-hook-form'
import type { WorkerFormData as Worker, WorkerFormArrayData } from '@/lib/schemas'
import type { WorkerSaveErrors } from '@/lib/utils/worker-save-errors'

/** A field the card shows, or `general` for the line at its head. */
export type WorkerCardSlot =
  | 'workerName'
  | 'jobCategory'
  | 'jobFunction'
  | 'jobTitle'
  | 'budgetedHours'
  | 'billRate'
  | 'reportingBillRate'
  | 'payRates'
  | 'paySchedule'
  | 'reportingPayRate'
  | 'firstDayOfWork'
  | 'lastDayOfWork'
  | 'general'

/** The card field a schema path reports under. A field the card has no place for goes to its head. */
const SLOT_OF_FIELD: Partial<Record<keyof Worker, WorkerCardSlot>> = {
  workerUserProfileId: 'workerName',
  jobCategoryCode: 'jobCategory',
  jobFunctionCode: 'jobFunction',
  jobTitle: 'jobTitle',
  budgetedHours: 'budgetedHours',
  scheduledBudgetedHours: 'budgetedHours',
  billRate: 'billRate',
  billRateUnit: 'billRate',
  reportingBillRate: 'reportingBillRate',
  payRates: 'payRates',
  payRate: 'payRates',
  payRateUnit: 'payRates',
  scheduledPayRate: 'payRates',
  paySchedule: 'paySchedule',
  reportingPayRate: 'reportingPayRate',
  scheduledReportingPayRate: 'reportingPayRate',
  firstDayOfWork: 'firstDayOfWork',
  lastDayOfWork: 'lastDayOfWork',
}

/** What the workers form holds against one worker. A field's own error and a list's errors share it. */
export type WorkerFieldErrors = NonNullable<FieldErrors<WorkerFormArrayData>['workers']>[number]

/** A worker's errors, by the card field they sit under. */
export interface WorkerCardErrors {
  bySlot: Partial<Record<WorkerCardSlot, string[]>>
  /** Errors about one of the worker's rates, by its position in `payRates`. */
  byRate: Record<number, string[]>
}

interface FlatError {
  path: string[]
  message: string
}

/**
 * Every message in a React Hook Form error tree, with the path it sits at. An array field carries an
 * issue about the list as a whole on `root`, beside its entries, and every error carries the element it
 * is bound to on `ref`, which is not walked.
 */
const flatten = (errors: unknown, path: string[] = []): FlatError[] => {
  if (!errors || typeof errors !== 'object') {
    return []
  }

  const found: FlatError[] = []
  const { message } = errors as { message?: unknown }
  if (typeof message === 'string' && message) {
    found.push({ path, message })
  }

  for (const [key, value] of Object.entries(errors)) {
    if (key === 'ref' || key === 'message' || key === 'type' || key === 'types') {
      continue
    }
    found.push(...flatten(value, key === 'root' ? path : [...path, key]))
  }

  return found
}

const add = <K extends string | number>(into: Partial<Record<K, string[]>>, key: K, message: string) => {
  const messages = (into[key] ??= [])
  if (!messages.includes(message)) {
    messages.push(message)
  }
}

/**
 * What the worker schema and a refused save each say about one worker, by the card field they are about.
 * A field the schema already flags shows the schema's message alone: it is checked live against the
 * worker as it stands, where the refusal describes the worker as it was saved.
 */
export const workerCardErrors = (
  fieldErrors: WorkerFieldErrors | undefined,
  saveErrors: WorkerSaveErrors | undefined
): WorkerCardErrors => {
  const bySlot: WorkerCardErrors['bySlot'] = {}
  const byRate: WorkerCardErrors['byRate'] = {}

  for (const { path, message } of flatten(fieldErrors)) {
    const [field, index] = path
    if (field === 'payRates' && index !== undefined && /^\d+$/.test(index)) {
      add(byRate, Number(index), message)
      continue
    }
    add(bySlot, SLOT_OF_FIELD[field as keyof Worker] ?? 'general', message)
  }

  for (const [slot, message] of Object.entries(saveErrors ?? {}) as [WorkerCardSlot, string][]) {
    const flaggedLive = slot === 'payRates'
      ? Boolean(bySlot.payRates?.length) || Object.keys(byRate).length > 0
      : Boolean(bySlot[slot]?.length)

    if (slot === 'general' || !flaggedLive) {
      add(bySlot, slot, message)
    }
  }

  return { bySlot, byRate }
}
